import { Axios } from "axios";
import { useState, useCallback, useEffect } from "react";
import { APIError } from "../core/_axios";
import { managementGetAccountFastByUniqueIdentifier } from "../core/_requests";
import { getCurrentAccount } from "../core/_requests_v2";
import { AccountFast } from "../core/_models_fast";
import { useAuth } from "../AuthContext";

export default function useAccount({
    axios,
    isForCurrentAccount = false,
    accountUniqueId = null
}: {
    axios: Axios;
    isForCurrentAccount?: boolean;
    accountUniqueId?: string | null;
}) {

    const {
        viewingAllAccounts
    } = useAuth();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [account, setAccount] = useState<AccountFast | null>(null);

    const onLoadAccount = useCallback(async () => {
        if (isForCurrentAccount) {
            if (viewingAllAccounts) {
                setLoading(false);
                setLoadingError('You cannot load the current account while viewing all accounts.');
                return;
            }
            setLoading(true);
            setLoadingError(null);
            try {
                const currentAccount = await getCurrentAccount(axios);
                setAccount(currentAccount);
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                setLoadingError(errorMessage)
            } finally {
                setLoading(false)
            }
        } else if (accountUniqueId) {
            if (!accountUniqueId) {
                setLoading(false);
                setLoadingError('No account unique identifier provided.');
                return;
            }
            setLoading(true);
            setLoadingError(null);
            try {
                const updatedAccount = await managementGetAccountFastByUniqueIdentifier(axios, accountUniqueId);
                setAccount(updatedAccount);
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                setLoadingError(errorMessage)
            } finally {
                setLoading(false)
            }
        } else {
            setLoading(false);
            setLoadingError('No account unique identifier provided.');
        }
    }, [axios, accountUniqueId, isForCurrentAccount, viewingAllAccounts]);

    useEffect(() => {
        onLoadAccount();
    }, [onLoadAccount]);

    return {
        loading,
        loadingError,
        account,
        setAccount,
        onLoadAccount
    };
}